/**
 * The anchor coordinate system — types and frozen constants.
 * SPEC §3.1, §3.2, §3.3.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS A FROZEN CONTRACT.
 *
 * Every remark a user has ever written is addressed in this coordinate system,
 * and every backup file ever exported carries `ref` strings minted by it. A
 * change here is not a refactor; it is a migration that must run on every
 * installed device, forever, including devices that have been offline for two
 * years. Add to it. Do not reinterpret it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Three layers (SPEC §3.1):
 *
 *   Layer 1 — truth, and the only anchor field written to the backup file:
 *             a string `ref`, e.g. `hafs:2:255:8-13`.
 *   Layer 2 — indexed, derived: `Pos = ayahId * WORD_SLOTS + wordIndex`, with
 *             `ayahId ∈ 1..6236` from the frozen 115-entry cumulative offset
 *             table in `offsets.ts`.
 *   Layer 3 — integrity, derived and stored: `quote` (≤200 chars) plus
 *             `text_hash_raw = sha256(span)` and
 *             `text_hash_fold = sha256(fold(span))`, both truncated to 16 hex
 *             characters, plus `anchor_space_version`.
 *
 * Everything below Layer 1 is rebuilt from `ref` on import. That is what lets a
 * v1.0 export import correctly into v4.0, and what lets somebody read the file
 * in 2040 with `jq` and no copy of this codebase.
 */

/**
 * The version of the anchor space: the pair (canonical segmentation, frozen
 * word-count fixture). Bumping it is a declaration that word indices minted
 * under the old value may no longer mean the same words, and forces review of
 * every imported `words` anchor (SPEC §3.2, row 6).
 *
 * Whole-ayah and range anchors are unaffected by a bump — they carry no word
 * span — which is why the ~90% case can never break.
 */
export const ANCHOR_SPACE_VERSION = 'aw-1.0.0';

/** Hafs 'an 'Asim, Kufan count. The only numbering this app has ever used. */
export const NUMBERING = 'hafs' as const;

/** Total ayat in the Hafs/Kufan count. Asserted against `offsets.ts` in tests. */
export const TOTAL_AYAT = 6236;

/** Surahs. */
export const SURAH_COUNT = 114;

/**
 * Word slots reserved per ayah. The longest ayah, 2:282, is 128 canonical
 * words, so the slot is ~8x oversized on purpose: a tokenisation correction is
 * confined to the one ayah it touched instead of shifting every subsequent
 * word in the mushaf (SPEC §3.1, "Why sparse and not a dense global ordinal").
 *
 * DO NOT CHANGE. Every stored `start_pos`/`end_pos` in every database is
 * `ayahId * 1024 + word`; a different multiplier silently repoints all of them.
 */
export const WORD_SLOTS = 1024;

/** `6236 * 1024 + 1023`. Comfortably inside `Number.MAX_SAFE_INTEGER`. */
export const MAX_POS = TOTAL_AYAT * WORD_SLOTS + (WORD_SLOTS - 1);

/**
 * `start_pos`/`end_pos` for anchors that have no place in the mushaf
 * (`external`, `free`). `-1`, never `null` and never `undefined`: IndexedDB
 * cannot index `null`, and a row whose indexed keypath is `null` is simply
 * ABSENT from that index (SPEC §3.3, invariant 3). `-1` sorts before every
 * real `Pos` and is therefore excluded by every `between(firstPos, …)` range
 * query for free.
 */
export const NO_POS = -1;

/** The maximum length, in code points, of a stored `quote`. SPEC §3.1. */
export const QUOTE_MAX_CHARS = 200;

/**
 * There is deliberately NO `'page'` member. Pages are a rendering, not a place:
 * they differ between mus'haf editions, between the QCF layout and a reflowing
 * one, and between font sizes on the same device. A future contributor must not
 * be able to reach for one.
 */
export type AnchorKind = 'ayah' | 'range' | 'words' | 'external' | 'free';

/** 1..6236. Zero is not a valid ayahId; the offset tables pad index 0. */
export type AyahId = number;

/** `ayahId * WORD_SLOTS + wordIndex`, or `NO_POS`. */
export type Pos = number;

/** The five corpora an `ext:` anchor may name. */
export type AnchorSource = 'azkar' | 'dua' | 'asma' | 'prayer_log' | 'fast_log';

/**
 * Structurally identical to `Anchor` in SPEC §3.3's `src/lib/db/schema.ts`
 * block. `schema.ts` should re-export these rather than redeclare them, so
 * there is exactly one definition of the coordinate system in the codebase.
 */
export interface Anchor {
	kind: AnchorKind;
	numbering: 'hafs';

	/** THE TRUTH. Durable, portable, the only anchor field in the backup file.
	 *  'hafs:2:255'                        whole ayah
	 *  'hafs:2:255-2:257'                  ayah range (may cross surah)
	 *  'hafs:2:255:8-13'                   canonical words 8..12 (end exclusive)
	 *  'ext:azkar.morning.0007@2026.09.1'  non-Qur'an corpus item
	 *  'ext:prayer_log:2026-09-08:fajr'    a reflection on a logged prayer
	 *  'free:'                             an unanchored remark
	 *  Everything below is DERIVED from `ref` and rebuilt on import. */
	ref: string;

	surah?: number;
	ayah?: number;
	end_surah?: number;
	end_ayah?: number;
	/** 0-based, canonical, inclusive. */
	word_start?: number;
	/** exclusive. */
	word_end?: number;

	source?: AnchorSource;
	source_id?: string;
	corpus_version?: string;

	/* integrity — the self-healing set */
	/** ≤200 chars, verbatim, captured at write time. */
	quote?: string;
	/** sha256(span).slice(0,16) — verbatim, WITH diacritics. */
	text_hash_raw?: string;
	/** sha256(fold(span)).slice(0,16) — normalised. */
	text_hash_fold?: string;
	/** ANCHOR_SPACE_VERSION at mint time. */
	anchor_space_version?: string;
}

/** Mixed into every annotatable row. `anchor` is truth; the rest are indexes. */
export interface Anchored {
	anchor: Anchor;
	/** inclusive; NO_POS for 'external' and 'free'. */
	start_pos: Pos;
	/** inclusive; NO_POS for 'external' and 'free'. */
	end_pos: Pos;
	/** set at READ time. Never auto-fixed, never auto-cleared. */
	drift: 0 | 1;
}

/** A closed integer interval in `Pos` space. Both ends inclusive. */
export interface Span {
	start_pos: Pos;
	end_pos: Pos;
}

/** A surah/ayah pair. */
export interface Location {
	surah: number;
	ayah: number;
}

/**
 * Thrown by `parseRef` and by the offset conversions on invalid input. Carries
 * the offending string so the Repair screen (SPEC §3.2 tier 4) can show the
 * user the raw `ref` beside the stored `quote` without re-deriving anything.
 */
export class AnchorRefError extends Error {
	readonly ref: string;
	readonly code: string;

	constructor(code: string, ref: string, message: string) {
		super(`${message} (ref: ${JSON.stringify(ref)})`);
		this.name = 'AnchorRefError';
		this.code = code;
		this.ref = ref;
	}
}
